import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { postService, Post } from "../services/api";
import { Star, Clock, Globe } from "lucide-react";
import toast from "react-hot-toast";
import { SectionHeader } from "../components/SectionHeader";

const LANG_OPTIONS = [
  { label: "All", value: "all" },
  { label: "Español", value: "es" },
  { label: "English", value: "en" },
  { label: "Français", value: "fr" },
  { label: "Deutsch", value: "de" },
  { label: "Português", value: "pt" },
  { label: "Italiano", value: "it" },
  { label: "Русский", value: "ru" },
  { label: "Türkçe", value: "tr" },
  { label: "Polski", value: "pl" },
  { label: "Українська", value: "uk" },
  { label: "Nederlands", value: "nl" },
  { label: "Română", value: "ro" },
  { label: "Ελληνικά", value: "el" },
  { label: "Svenska", value: "sv" },
  { label: "Čeština", value: "cs" },
  { label: "Magyar", value: "hu" },
  { label: "Srpskohrvatski", value: "sh" },
  { label: "Български", value: "bg" },
  { label: "Suomi", value: "fi" },
  { label: "Dansk", value: "da" },
  { label: "Slovenčina", value: "sk" },
  { label: "Norsk", value: "no" },
];

const Frontpage: React.FC = () => {
  const [featuredPosts, setFeaturedPosts] = useState<Post[]>([]);
  const [recentPosts, setRecentPosts] = useState<Post[]>([]);
  const [popularHashtags, setPopularHashtags] = useState<{ hashtag: string; count: number }[]>([]);
  const [rankingPosts, setRankingPosts] = useState<Post[]>([]);
  const [heroPost, setHeroPost] = useState<Post | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedLang, setSelectedLang] = useState("all");

  useEffect(() => {
    const saved = localStorage.getItem("frontpage_lang");
    if (saved) setSelectedLang(saved);
  }, []);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);

      const languagesParam = selectedLang === "all" ? undefined : [selectedLang];

      try {
        const data = await postService.getFrontpageAggregate(languagesParam);
        setHeroPost(data.hero);
        setFeaturedPosts(data.featured);
        setRecentPosts(data.recent);
        setRankingPosts(data.ranks);
        setPopularHashtags(data.hashtags);
      } catch (error) {
        toast.error("Error loading homepage");
        console.error(error);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [selectedLang]); // <- Se vuelve a ejecutar cuando cambia el idioma

  useEffect(() => {
    localStorage.setItem("frontpage_lang", selectedLang);
  }, [selectedLang]);

  const truncateContent = (content: string, maxLength: number = 200) => {
    if (content.length <= maxLength) return content;
    return content.substring(0, maxLength) + "...";
  };

  // Función específica para truncar títulos basándose en el ancho disponible (370px)
  // Con font-size: 20px y font-weight: 700, aproximadamente 85-90 caracteres caben en 370px
  const truncateTitle = (title: string, maxLength: number = 85) => {
    if (title.length <= maxLength) return title;
    return title.substring(0, maxLength) + "...";
  };

  const LanguageSelector = () => (
    <div className="flex items-center gap-3 mb-6">
      <Globe className="w-5 h-5 text-gray-500" />
      <select
        value={selectedLang}
        onChange={(e) => setSelectedLang(e.target.value)}
        className="border border-gray-300 rounded-md px-2 py-1 text-sm bg-white text-gray-700"
      >
        {LANG_OPTIONS.map((opt) => (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        ))}
      </select>
    </div>
  );
  // Función de validación para determinar si un post puede ser heroPost
  const isValidHeroPost = (post: Post): boolean => {
    // 1. archived: false
    if (post.archived) return false;

    // 2. Longitud de contenido dentro de reglas (≥150 y ≤15,000)
    if (post.content.length < 150 || post.content.length > 15000) return false;

    // 3. El hero debe llevar imagen válida
    if (!post.image || post.image.trim() === "") return false;

    // Nota: No hay campos específicos para posts ocultos/baneados en la estructura actual
    // Los filtros de usuarios baneados se manejan en el backend

    return true;
  };

  // Función para encontrar el primer post válido en una lista
  const findValidHeroPost = (posts: Post[]): Post | undefined => {
    return posts.find((post) => isValidHeroPost(post));
  };

  // Derived groupings to match Figma sections

  const topRatedPosts: Post[] = (() => {
    const map = new Map<string, Post>();
    [...recentPosts, ...featuredPosts, ...rankingPosts].forEach((p) => {
      if (!map.has(p.id)) map.set(p.id, p);
    });
    const heroId = heroPost?.id;
    return Array.from(map.values())
      .filter((p) => p.id !== heroId)
      .sort((a, b) => {
        if (b.average_rating !== a.average_rating) return b.average_rating - a.average_rating;
        return b.visits - a.visits;
      })
      .slice(0, 4);
  })();

  const recentRestPosts: Post[] = (() => {
    const exclude = new Set<string>([heroPost?.id || "", ...topRatedPosts.map((p) => p.id)]);
    // Los 3 posts más recientes por fecha, excluyendo hero/topRated
    return recentPosts.filter((p) => !exclude.has(p.id)).slice(0, 9);
  })();

  // Detectar si no hay contenido en el idioma seleccionado
  const hasNoContent =
    !loading &&
    featuredPosts.length === 0 &&
    recentPosts.length === 0 &&
    rankingPosts.length === 0 &&
    selectedLang !== "all";

  // Obtener el nombre del idioma seleccionado para el mensaje
  const getLanguageName = (langCode: string): string => {
    const langOption = LANG_OPTIONS.find((opt) => opt.value === langCode);
    return langOption ? langOption.label : langCode;
  };

  if (loading) {
    return (
      <div className="beat-container">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Loading skeleton */}
          {[...Array(6)].map((_, i) => (
            <div key={i} className="beat-card p-6">
              <div className="beat-loading h-6 mb-4"></div>
              <div className="beat-loading h-4 mb-2"></div>
              <div className="beat-loading h-4 mb-2"></div>
              <div className="beat-loading h-32"></div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  // Mostrar mensaje cuando no hay contenido en el idioma seleccionado
  if (hasNoContent) {
    return (
      <div className="SectionsContainer beat-container w-full">
        {/* Header */}
        <div
          style={{
            width: "100%",
            background: "rgba(0, 0, 0, 0)",
            flexDirection: "column",
            justifyContent: "flex-start",
            alignItems: "center",
            gap: "17px",
            display: "flex",
            paddingTop: "17px",
            paddingBottom: "17px",
          }}
        >
          <p
            style={{
              color: "#525252",
              fontSize: "18px",
              fontFamily: "Instrument Sans",
              fontWeight: "400",
              wordWrap: "break-word",
            }}
          >
            I've got my Mojo working, this is Beatpost's Frontpage.
          </p>
          <LanguageSelector />
        </div>

        {/* Mensaje de no contenido */}
        <div
          style={{
            width: "100%",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            padding: "80px 20px",
            textAlign: "center",
          }}
        >
          <div
            style={{
              color: "#737373",
              fontSize: "24px",
              fontFamily: "Abhaya Libre",
              fontWeight: "600",
              marginBottom: "16px",
              wordWrap: "break-word",
            }}
          >
            No articles in {getLanguageName(selectedLang)} yet
          </div>
          <div
            style={{
              color: "#A3A3A3",
              fontSize: "16px",
              fontFamily: "Instrument Sans",
              fontWeight: "400",
              lineHeight: "24px",
              maxWidth: "400px",
              wordWrap: "break-word",
            }}
          >
            It looks like we don’t have any content available in this language yet. Try selecting “All” to view every
            article.
          </div>
          <button
            onClick={() => setSelectedLang("all")}
            style={{
              marginTop: "24px",
              padding: "12px 24px",
              backgroundColor: "#262626",
              color: "white",
              border: "none",
              borderRadius: "6px",
              fontSize: "14px",
              fontFamily: "Instrument Sans",
              fontWeight: "500",
              cursor: "pointer",
              transition: "background-color 0.2s",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = "#404040";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = "#262626";
            }}
          >
            View all articles
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="SectionsContainer beat-container w-full">
      {/* <SectionHeader
        subtitle="I’ve got my Mojo working, this is Beatpost’s Frontpage."
      />
      /> */}
      {/* Header */}
      <div
        style={{
          width: "100%",
          background: "rgba(0, 0, 0, 0)",
          flexDirection: "column",
          justifyContent: "flex-start",
          alignItems: "center",
          gap: "17px",
          display: "flex",
          paddingTop: "17px",
          paddingBottom: "17px",
        }}
      >
        <p
          style={{
            color: "#525252",
            fontSize: "18px",
            fontFamily: "Instrument Sans",
            fontWeight: "400",
            wordWrap: "break-word",
          }}
        >
          I’ve got my Mojo working, this is Beatpost’s Frontpage.
        </p>
        <LanguageSelector />
      </div>
      {/* Sección 1: Hero + The Beatline */}
      {heroPost && (
        <section className="heroPostSection mb-12">
          <div className="flex xl:flex-row flex-col py-6 md:py-8 xl:py-10 border-y border-[#D3D3D3]">
            {/* HeroPost Content */}
            <div className="lg:col-span-3 w-[-webkit-fill-available]" id="hero-post">
              <div
                className="w-full xl:h-[610px] flex flex-col items-center gap-4 md:gap-5"
                style={{
                  justifyContent: "flex-start",
                  alignItems: "center",
                }}
              >
                {/* Imagen */}
                {heroPost.image ? (
                  <div className="featured-image-wrapper w-full h-[375px] md:h-[420px] xl:h-[610px] overflow-hidden bg-white flex items-center justify-center">
                    <img
                      src={heroPost.image}
                      alt=""
                      className="h-full w-auto object-contain beat-image-bw"
                      // @ts-ignore
                      fetchpriority="high"
                    />
                  </div>
                ) : (
                  <div className="featured-image-wrapper w-full h-[375px] md:h-[420px] xl:h-[610px] overflow-hidden bg-white flex items-center justify-center">
                    <img
                      src="/Beatpost_defaut_image.png"
                      alt="Beatpost default image"
                      className="h-full w-auto object-contain beat-image-bw"
                    />
                  </div>
                )}

                {/* Título */}
                <Link to={`/post/${heroPost.id}`}>
                  <div
                    className="w-full text-center text-[28px] leading-[32px] md:text-[40px] md:leading-[40px] xl:text-[48px] xl:leading-[45px]"
                    style={{
                      color: "#262626",
                      fontFamily: "Abhaya Libre",
                      fontWeight: "400",
                      wordWrap: "break-word",
                    }}
                  >
                    {heroPost.title}
                  </div>
                </Link>

                {/* Descripción */}
                <div
                  style={{
                    alignSelf: "stretch",
                    height: "max-content",
                    textAlign: "center",
                    color: "#525252",
                    fontSize: "18px",
                    fontFamily: "Instrument Sans",
                    fontWeight: "400",
                    lineHeight: "28px",
                    wordWrap: "break-word",
                  }}
                >
                  {truncateContent(heroPost.content, 200)}
                </div>

                {/* Metadata */}
                <div
                  className="w-max hero-post-author flex flex-wrap justify-center"
                  style={{
                    justifyContent: "flex-start",
                    alignItems: "center",
                    gap: "13px",
                    display: "inline-flex",
                    fontSize: "14px",
                    fontFamily: "Abhaya Libre",
                    fontWeight: "400",
                    lineHeight: "16px",
                    letterSpacing: 0.6,
                    wordWrap: "break-word",
                  }}
                >
                  {/* Avatar */}
                  <img
                    src={heroPost.author_avatar || "/default-avatar.png"} // fallback si no hay avatar
                    alt={heroPost.author_username}
                    className="w-6 h-6 rounded-full object-cover"
                  />

                  {/* Autor */}
                  <Link to={`/voice/${heroPost.author_username}`}>
                    <div
                      style={{
                        color: "#737373",
                      }}
                    >
                      By {heroPost.author_username}
                    </div>
                  </Link>

                  {/* Separador */}
                  <div
                    className="hidden sm:block"
                    style={{
                      width: "5px",
                      height: "16px",
                      textAlign: "center",
                      color: "#D4D4D4",
                      fontSize: "13px",
                      fontFamily: "Inter",
                      fontWeight: "400",
                      lineHeight: "16px",
                      letterSpacing: 0.6,
                      wordWrap: "break-word",
                    }}
                  >
                    |
                  </div>

                  {/* Hashtags */}
                  {heroPost.hashtags.slice(0, 3).map((tag, index) => (
                    <React.Fragment key={tag}>
                      <Link to={`/thepulse?hashtag=${tag}`}>
                        <div
                          style={{
                            textAlign: "center",
                            color: "#737373",
                          }}
                        >
                          #{tag}
                        </div>
                      </Link>
                    </React.Fragment>
                  ))}

                  {/* Separador */}
                  <div
                    id="hashtag-separator"
                    className="hidden sm:block"
                    style={{
                      width: "5px",
                      height: "16px",
                      textAlign: "center",
                      color: "#D4D4D4",
                      fontSize: "13px",
                      fontFamily: "Inter",
                      fontWeight: "400",
                      lineHeight: "16px",
                      letterSpacing: 0.6,
                      wordWrap: "break-word",
                    }}
                  >
                    |
                  </div>

                  {/* Rating */}
                  <div
                    style={{
                      background: "rgba(0, 0, 0, 0)",
                      justifyContent: "flex-start",
                      alignItems: "center",
                      gap: "7px",
                      display: "flex",
                    }}
                  >
                    <Star className="w-4 h-4" style={{ color: "#737373" }} />
                    <div
                      style={{
                        textAlign: "center",
                        color: "#737373",
                      }}
                    >
                      {heroPost.average_rating.toFixed(1)}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Separador */}
            <div
              className="hidden lg:block"
              style={{
                width: "1px",
                backgroundColor: "#D3D3D3",
                marginLeft: "37px",
                marginRight: "37px",
              }}
            />

            {/* The Beatline */}
            {rankingPosts.length > 0 && (
              <aside>
                <div
                  className="top-ranking xl:w-[258px] w-full h-full xl:space-y-3 space-y-5 mt-6 md:mt-0"
                  style={{
                    flexDirection: "column",
                    alignItems: "flex-start",
                    display: "inline-flex",
                  }}
                >
                  <div
                    className="top-ranking-title"
                    style={{
                      alignSelf: "stretch",
                      color: "#171717",
                      fontSize: "24px",
                      fontFamily: "Playfair Display",
                      fontWeight: "600",
                      lineHeight: "31px",
                      wordWrap: "break-word",
                    }}
                  >
                    The Beatline
                  </div>
                  {rankingPosts.slice(0, 3).map((p, idx) => (
                    <div
                      key={p.id}
                      data-property-1="Default"
                      style={{
                        alignSelf: "stretch",
                        background: "rgba(0, 0, 0, 0)",
                        flexDirection: "column",
                        justifyContent: "flex-start",
                        alignItems: "flex-start",
                        gap: 8,
                        display: "flex",
                      }}
                    >
                      <div
                        className="top-ranking-item-tittle"
                        style={{
                          width: 257,
                          justifyContent: "flex-start",
                          alignItems: "flex-start",
                          gap: 8,
                          display: "inline-flex",
                        }}
                      >
                        <div
                          style={{
                            width: 15,
                            color: "black",
                            fontSize: "15px",
                            fontFamily: "Instrument Sans",
                            fontWeight: "700",
                            lineHeight: "25px",
                            wordWrap: "break-word",
                          }}
                        >
                          {idx + 1}
                          <br />-
                        </div>
                        <div
                          style={{
                            width: 234,
                            color: "#262626",
                            fontSize: "18px",
                            fontFamily: "Abhaya Libre",
                            fontWeight: "700",
                            lineHeight: "25px",
                            wordWrap: "break-word",
                          }}
                        >
                          <Link
                            to={`/post/${p.id}`}
                            style={{
                              color: "#262626",
                              textDecoration: "none",
                            }}
                            // onMouseEnter={(e) => e.target.style.textDecoration = "underline"}
                            // onMouseLeave={(e) => e.target.style.textDecoration = "none"}
                          >
                            {p.title}
                          </Link>
                        </div>
                      </div>
                      <div
                        className="top-ranking-item-content"
                        style={{
                          alignSelf: "stretch",
                          color: "#525252",
                          fontSize: "14px",
                          fontFamily: "Instrument Sans",
                          fontWeight: "400",
                          lineHeight: "20px",
                          wordWrap: "break-word",
                        }}
                      >
                        {truncateContent(p.content, 90)}
                      </div>
                      <div
                        className="top-ranking-item-meta"
                        style={{
                          alignSelf: "stretch",
                          background: "rgba(0, 0, 0, 0)",
                          justifyContent: "space-between",
                          alignItems: "center",
                          display: "inline-flex",
                        }}
                      >
                        <Link to={`/voice/${p.author_username}`}>
                          <div
                            className="flex flex-row items-center gap-2"
                            style={{
                              color: "#737373",
                              fontSize: "13px",
                              fontFamily: "Inter",
                              fontWeight: "400",
                              lineHeight: "16px",
                              letterSpacing: 0.6,
                              wordWrap: "break-word",
                            }}
                          >
                            <img
                              src={p.author_avatar || "/default-avatar.png"}
                              alt={p.author_username}
                              className="w-6 h-6 rounded-full object-cover"
                            />
                            By {p.author_username}
                          </div>
                        </Link>

                        <div className="inline-flex">
                          <Star className="w-4 h-4 mr-2 inline" />
                          <div
                            style={{
                              width: 20,
                              height: 16,
                              color: "#737373",
                              fontSize: "13px",
                              fontFamily: "Inter",
                              fontWeight: "400",
                              lineHeight: "16px",
                              letterSpacing: 0.6,
                              wordWrap: "break-word",
                            }}
                          >
                            {p.average_rating.toFixed(1)}
                          </div>
                        </div>
                      </div>
                      <div
                        className="top-ranking-item-hashtags"
                        style={{
                          alignSelf: "stretch",
                          background: "rgba(0, 0, 0, 0)",
                          justifyContent: "flex-start",
                          alignItems: "center",
                          gap: 11,
                          display: "inline-flex",
                        }}
                      >
                        {p.hashtags.slice(0, 3).map((hashtag, hashIdx) => (
                          <div
                            key={hashIdx}
                            style={{
                              color: "#A3A3A3",
                              fontSize: "13px",
                              fontFamily: "Abhaya Libre",
                              fontWeight: "400",
                              lineHeight: "16px",
                              wordWrap: "break-word",
                            }}
                          >
                            #{hashtag}
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </aside>
            )}
          </div>
        </section>
      )}

      {/* Sección 2: 4 publicaciones más votadas (mayor puntuación) */}
      {topRatedPosts.length > 0 && (
        <section className="mb-12 topRatedPosts">
          <h2
            className="text-newspaper-900 mb-6 flex items-center"
            style={{
              fontFamily: "Playfair Display",
              fontSize: "24px",
              fontWeight: "600",
              lineHeight: "31px",
              wordWrap: "break-word",
            }}
          >
            {/* <Star className="w-6 h-6 mr-2" /> */}
            The Select
          </h2>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {topRatedPosts.map((post, index) => (
              <article key={post.id} className="beat-post-card">
                <div className="flex flex-col sm:flex-row gap-5 sm:gap-7 items-start">
                  {/* Imagen cuadrada 169x169 */}
                  <div className="flex-shrink-0 w-full sm:w-auto">
                    {post.image ? (
                      <img
                        src={post.image}
                        alt=""
                        className="w-full h-[226px] sm:w-[169px] sm:h-[169px] object-contain beat-image-bw bg-[#D9D9D9]"
                      />
                    ) : (
                      <img
                        src="/Beatpost_defaut_image.png"
                        alt="Beatpost placeholder"
                        className="w-full h-[226px] sm:w-[169px] sm:h-[169px] object-contain beat-image-bw bg-[#D9D9D9]"
                      />
                    )}
                  </div>

                  {/* Contenido a la derecha */}
                  <div
                    className="flex flex-col flex-1"
                    style={{
                      height: "-webkit-fill-available",
                      width: "-webkit-fill-available",
                      justifyContent: "space-between",
                      paddingTop: "4px",
                    }}
                  >
                    {/* Título */}
                    <Link to={`/post/${post.id}`}>
                      <h3
                        className="font-serif font-bold hover:text-newspaper-700 transition-colors"
                        style={{
                          marginBottom: "12px",
                          color: "#262626",
                          fontSize: 20,
                          fontFamily: "Abhaya Libre",
                          fontWeight: "700",
                          wordWrap: "break-word",
                          lineHeight: "24px",
                        }}
                      >
                        {truncateTitle(post.title)}
                      </h3>
                    </Link>

                    {/* Descripción */}
                    <p
                      style={{
                        color: "#666666",
                        fontSize: "16px",
                        fontWeight: "400",
                        lineHeight: "24px",
                        wordWrap: "break-word",
                        marginBottom: "16px",
                        fontFamily: "Instrument Sans",
                      }}
                    >
                      {truncateContent(post.content, 120)}
                    </p>

                    {/* Metadata: Autor y Rating */}
                    <div className="flex justify-between items-center" style={{ marginBottom: "8px" }}>
                      <div
                        className="TopRatedAuthor flex flex-row items-center gap-2"
                        style={{
                          color: "#737373",
                          fontSize: "14px",
                          fontWeight: "400",
                          lineHeight: "18px",
                          wordWrap: "break-word",
                          fontFamily: "Abhaya Libre",
                        }}
                      >
                        <img
                          src={post.author_avatar || "/default-avatar.png"}
                          alt={post.author_username}
                          className="w-6 h-6 rounded-full object-cover"
                        />
                        <Link to={`/voice/${post.author_username}`} className="hover:text-newspaper-900">
                          By {post.author_username}
                        </Link>
                      </div>

                      <div className="flex items-center gap-[6px]">
                        <Star className="w-4 h-4" style={{ color: "#737373" }} />
                        <div
                          style={{
                            color: "#737373",
                            fontSize: "14px",
                            fontWeight: "400",
                            lineHeight: "18px",
                            wordWrap: "break-word",
                          }}
                        >
                          {post.average_rating.toFixed(1)}
                        </div>
                      </div>
                    </div>

                    {/* Hashtags */}
                    <div className="flex gap-3">
                      {post.hashtags.slice(0, 3).map((tag) => (
                        <Link
                          key={tag}
                          to={`/thepulse?hashtag=${tag}`}
                          className="hover:text-newspaper-700"
                          style={{
                            color: "#737373",
                            fontSize: "14px",
                            fontFamily: "Abhaya Libre",
                            fontWeight: "400",
                            lineHeight: "18px",
                            wordWrap: "break-word",
                          }}
                        >
                          #{tag}
                        </Link>
                      ))}
                    </div>
                  </div>
                </div>
              </article>
            ))}
          </div>
        </section>
      )}

      {/* Sección 3: Resto de publicaciones recientes (desde la 5ª), máximo 3 */}
      <section className="Resto de publicaciones recentPostsSection">
        <h2
          className="text-newspaper-900 mb-6 flex items-center"
          style={{
            fontFamily: "Playfair Display",
            fontSize: "24px",
            fontWeight: "600",
            lineHeight: "31px",
            wordWrap: "break-word",
          }}
        >
          {/* <Clock className="w-6 h-6 mr-2" /> */}
          Recent Posts
        </h2>

        {/* Layout vertical: imagen arriba, texto abajo */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {recentRestPosts.map((post) => (
            <article key={post.id} className="beat-post-card">
              {/* Layout vertical */}
              <div
                style={{
                  background: "rgba(0, 0, 0, 0)",
                  flexDirection: "column",
                  justifyContent: "flex-start",
                  alignItems: "flex-start",
                  gap: 28,
                  display: "inline-flex",
                  width: "100%",
                }}
              >
                {/* Imagen arriba - proporción 5:3 (≈1.67) */}
                {post.image ? (
                  <img
                    src={post.image}
                    alt=""
                    className="beat-image-bw"
                    style={{
                      width: "100%",
                      height: 226,
                      background: "#D9D9D9",
                      objectFit: "cover",
                    }}
                  />
                ) : (
                  <img
                    src="/Beatpost_defaut_image.png"
                    alt="Beatpost placeholder"
                    className="w-full h-[226px] object-contain beat-image-bw bg-[#D9D9D9]"
                  />
                )}

                {/* Bloque de texto abajo */}
                <div
                  style={{
                    width: "100%",
                    background: "rgba(0, 0, 0, 0)",
                    flexDirection: "column",
                    justifyContent: "flex-start",
                    alignItems: "flex-start",
                    gap: 9,
                    display: "flex",
                  }}
                >
                  {/* Título */}
                  <Link to={`/post/${post.id}`}>
                    <div
                      className="hover:text-newspaper-700 transition-colors"
                      style={{
                        alignSelf: "stretch",
                        color: "#262626",
                        fontSize: 20,
                        fontFamily: "Abhaya Libre",
                        fontWeight: "700",
                        wordWrap: "break-word",
                        lineHeight: "24px",
                      }}
                    >
                      {post.title}
                    </div>
                  </Link>

                  {/* Descripción/bajada */}
                  <div
                    style={{
                      alignSelf: "stretch",
                      color: "#525252",
                      fontSize: "14px",
                      fontFamily: "Instrument Sans",
                      fontWeight: "400",
                      wordWrap: "break-word",
                    }}
                  >
                    {truncateContent(post.content, 120)}
                  </div>

                  {/* Meta: autor y rating con space-between */}
                  <div
                    className="recent-posts-meta"
                    style={{
                      alignSelf: "stretch",
                      background: "rgba(0, 0, 0, 0)",
                      justifyContent: "space-between",
                      alignItems: "center",
                      display: "inline-flex",
                    }}
                  >
                    <Link
                      to={`/voice/${post.author_username}`}
                      className="RecentPostAuthor hover:text-newspaper-700 transition-colors flex flex-row gap-2 items-center"
                      style={{
                        height: 16,
                        color: "#737373",
                        fontSize: "14px",
                        fontFamily: "Abhaya Libre",
                        fontWeight: "400",
                        letterSpacing: 0.6,
                        wordWrap: "break-word",
                      }}
                    >
                      <img
                        src={post.author_avatar || "/default-avatar.png"}
                        alt={post.author_username}
                        className="w-6 h-6 rounded-full object-cover"
                      />
                      By {post.author_username}
                    </Link>
                    <div
                      style={{
                        background: "rgba(0, 0, 0, 0)",
                        justifyContent: "flex-start",
                        alignItems: "center",
                        gap: "7px",
                        display: "flex",
                      }}
                    >
                      <Star className="w-4 h-4" style={{ color: "#737373" }} />
                      <div
                        style={{
                          textAlign: "center",
                          color: "#737373",
                          fontSize: "13px",
                          fontFamily: "Instrument Sans",
                          fontWeight: "400",
                          letterSpacing: 0.6,
                          wordWrap: "break-word",
                        }}
                      >
                        {post.average_rating.toFixed(1)}
                      </div>
                    </div>
                  </div>

                  {/* Tags con separación de ~12px */}
                  <div className="recent-posts-tag-containe flex gap-3">
                    {post.hashtags.slice(0, 3).map((tag, index) => (
                      <Link
                        key={tag}
                        to={`/thepulse?hashtag=${tag}`}
                        className="hover:text-newspaper-500 transition-colors"
                        style={{
                          color: "#A3A3A3",
                          fontSize: "14px",
                          fontFamily: "Abhaya Libre",
                          fontWeight: "400",
                          lineHeight: "18px",
                          wordWrap: "break-word",
                        }}
                      >
                        #{tag}
                      </Link>
                    ))}
                  </div>
                </div>
              </div>
            </article>
          ))}
        </div>
      </section>
    </div>
  );
};

export default Frontpage;
